import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X } from "lucide-react";
import { api } from "@/lib/api";
import { STAYS } from "@/constants/testIds";
import PropertyCard from "@/components/PropertyCard";

const PRICE_BUCKETS = [
  { label: "Up to ₹15,000", min: 0, max: 15000 },
  { label: "₹15,000 - ₹20,000", min: 15000, max: 20000 },
  { label: "₹20,000 - ₹25,000", min: 20000, max: 25000 },
  { label: "₹25,000+", min: 25000, max: 99999999 },
];

export default function AllStays() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => ({
    destination: params.get("destination") || "",
    experience:  params.get("experience")  || "",
    bucket:      params.get("bucket")      || "",
    search:      params.get("search")      || "",
  }), [params]);

  useEffect(() => {
    api.get("/destinations").then(r => setDestinations(r.data));
    api.get("/experiences").then(r => setExperiences(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (filters.destination) q.destination = filters.destination;
    if (filters.experience)  q.experience  = filters.experience;
    if (filters.search)      q.search      = filters.search;
    if (filters.bucket) {
      const b = PRICE_BUCKETS[+filters.bucket];
      if (b) { q.min_price = b.min; q.max_price = b.max; }
    }
    api.get("/properties", { params: q })
      .then(r => setItems(r.data))
      .finally(()=>setLoading(false));
  }, [filters]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np, { replace: true });
  };
  const reset = () => setParams(new URLSearchParams(), { replace: true });

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen">
      <div className="container-tarzan">
        <div className="max-w-2xl">
          <div className="eyebrow mb-3">The Collection</div>
          <h1 className="font-serif text-5xl md:text-6xl text-ink tracking-tight leading-[1.02]">All Stays</h1>
          <p className="text-ink-soft mt-4 text-base md:text-lg leading-relaxed">Every home, personally vetted. Filter by destination, experience or budget to find yours.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
          <aside className="lg:sticky lg:top-28 self-start space-y-7 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto pr-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-forest font-medium"><Filter className="h-4 w-4" /> Filters</div>
              <button data-testid={STAYS.filterReset} onClick={reset} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-forest flex items-center gap-1">Reset <X className="h-3 w-3" /></button>
            </div>

            <FilterGroup label="Destination" testId={STAYS.filterDestination}>
              <div className="flex flex-wrap gap-2">
                {destinations.map(d => (
                  <Chip key={d.slug} active={filters.destination === d.name} onClick={()=>setFilter("destination", filters.destination === d.name ? "" : d.name)}>{d.name}</Chip>
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Experience" testId={STAYS.filterExperience}>
              <div className="flex flex-wrap gap-2">
                {experiences.map(e => (
                  <Chip key={e.slug} active={filters.experience === e.slug} onClick={()=>setFilter("experience", filters.experience === e.slug ? "" : e.slug)}>{e.name}</Chip>
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Price per night" testId={STAYS.filterPrice}>
              <div className="space-y-2">
                {PRICE_BUCKETS.map((b, i) => (
                  <label key={i} className="flex items-center gap-3 cursor-pointer">
                    <input type="radio" name="bucket" checked={filters.bucket === String(i)} onChange={()=>setFilter("bucket", String(i))} className="accent-forest" />
                    <span className="text-sm text-ink">{b.label}</span>
                  </label>
                ))}
              </div>
            </FilterGroup>
          </aside>

          <section>
            <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-ink-soft">{loading ? "Searching…" : `${items.length} stays`}</div>
        </div>
        <div className="mb-6 rounded-2xl bg-forest-50/60 border border-forest/10 px-5 py-4 text-sm text-forest-700 leading-relaxed">
          <strong className="text-forest">Now bookable:</strong> Tarzan Hyderabad Retreat. Every other property is joining the collection soon — enquire via the concierge to get on the priority list.
        </div>
            {!loading && items.length === 0 && (
              <div className="border border-dashed border-forest/20 rounded-3xl p-16 text-center">
                <div className="font-serif text-2xl text-ink">No stays match those filters.</div>
                <p className="text-ink-soft mt-2 text-sm">Try removing a filter or browse the full collection.</p>
                <button onClick={reset} className="mt-5 bg-forest text-ivory px-6 py-2.5 rounded-full text-sm">Reset filters</button>
              </div>
            )}
            <div data-testid={STAYS.pageGrid} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-7">
              {items.map(p => <PropertyCard key={p.id} p={p} />)}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft font-medium mb-3">{label}</div>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-forest text-ivory border-forest" : "bg-transparent text-ink border-forest/20 hover:border-forest"}`}>
      {children}
    </button>
  );
}
